import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import TariffEditModal from './TariffEditModal';

const URL_SCHEMA = {
  type: 'all',           // all | school | kindergarten
  archive: '0',          // 0 — только активные, 1 — включая архив
  edit: null,            // 'new' | id
};

const TYPE_LABELS = { school: 'Школа', kindergarten: 'Детский сад' };

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('ru-RU') + ' ₽';
}

export default function TariffsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('tariffs', 'write');
  const canDelete = can('tariffs', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = new URLSearchParams();
    if (params.type !== 'all') qs.set('type', params.type);
    if (params.archive === '1') qs.set('include_archive', '1');
    api.get('/api/tariffs?' + qs.toString())
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.type, params.archive]);

  const onDelete = async (t) => {
    if (!canDelete) return;
    if (!confirm(`Удалить тариф «${t.name}»?\nДоговоры с этим тарифом продолжат работать (tariff_id обнулится).`)) return;
    try {
      await api.delete(`/api/tariffs/${t.id}`);
      setRows((prev) => prev.filter((r) => r.id !== t.id));
    } catch (e) {
      alert('Не удалось удалить: ' + (e.message || ''));
    }
  };

  const editing = params.edit === 'new'
    ? 'new'
    : (params.edit ? rows.find((r) => String(r.id) === String(params.edit)) : null);

  return (
    <>
      <Topbar
        breadcrumb={['Финансы', 'Тарифы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Тарифы {!loading && <span className="muted">· {rows.length}</span>}
          </span>

          <div className="segmented">
            <button
              className={'segmented__btn segmented__btn--text' + (params.type === 'all' ? ' is-active' : '')}
              onClick={() => setParams({ type: 'all' })}
            >Все</button>
            <button
              className={'segmented__btn segmented__btn--text' + (params.type === 'school' ? ' is-active' : '')}
              onClick={() => setParams({ type: 'school' })}
            >Школа</button>
            <button
              className={'segmented__btn segmented__btn--text' + (params.type === 'kindergarten' ? ' is-active' : '')}
              onClick={() => setParams({ type: 'kindergarten' })}
            >Сад</button>
          </div>

          <label className="ref-checkbox" style={{ marginLeft: 8 }}>
            <input
              type="checkbox"
              checked={params.archive === '1'}
              onChange={(e) => setParams({ archive: e.target.checked ? '1' : '0' })}
            />
            <span>включая архив</span>
          </label>

          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет права на создание'}
          >
            <Icon name="plus" size={13} /> Добавить тариф
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Тарифов нет.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th style={{ width: 120 }}>Тип</th>
                    <th style={{ width: 140 }}>Цена / мес.</th>
                    <th style={{ width: 140 }}>Время</th>
                    <th style={{ width: 100 }}>Статус</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id}>
                      <td><span style={{ fontWeight: 500 }}>{t.name}</span></td>
                      <td>{TYPE_LABELS[t.type] || t.type}</td>
                      <td className="mono">{fmtMoney(t.price)}</td>
                      <td className="mono">{t.timeStart}–{t.timeEnd}</td>
                      <td>
                        {t.archive
                          ? <span className="sb sb--archived">Архив</span>
                          : <span className="sb sb--active">Активен</span>}
                      </td>
                      <td>
                        <div className="ref-row-actions">
                          <button
                            onClick={() => setParams({ edit: String(t.id) })}
                            title={canWrite ? 'Редактировать' : 'Просмотр'}
                          >
                            <Icon name="edit" size={14} />
                          </button>
                          {canDelete && (
                            <button className="is-danger" onClick={() => onDelete(t)} title="Удалить">
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing === 'new' && (
        <TariffEditModal
          mode="create"
          onClose={() => setParams({ edit: null })}
          onSaved={(t) => { setRows((prev) => [t, ...prev]); }}
        />
      )}
      {editing && editing !== 'new' && (
        <TariffEditModal
          mode="edit"
          tariff={editing}
          onClose={() => setParams({ edit: null })}
          onSaved={(t) => setRows((prev) => prev.map((r) => r.id === t.id ? t : r))}
        />
      )}
    </>
  );
}
